import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Send, Users, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export function DigestCard() {
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [log, setLog] = useState([]);
  const [recipients, setRecipients] = useState(null);
  const [showList, setShowList] = useState(false);

  const loadLog = useCallback(() => {
    api.get("/admin/digest/log").then(({ data }) => setLog(data.sends || [])).catch(() => {});
  }, []);
  useEffect(() => {
    loadLog();
    api.get("/admin/digest/recipients").then(({ data }) => setRecipients(data)).catch(() => {});
  }, [loadLog]);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/digest/send-test");
      if (data.has_content) toast.success(`Preview digest sent to ${data.sent_to}`);
      else toast.info("Preview sent — but there's no new activity this week yet");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSendingTest(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("Send this week's digest to EVERY account (free + paying) right now?")) return;
    setSendingNow(true);
    try {
      const { data } = await api.post("/admin/digest/send-now");
      toast.success(`Digest sent to ${data.total} accounts (${data.queued} accepted, ${data.failed} failed)`);
      loadLog();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSendingNow(false); }
  };

  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="digest-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-gold" />
        <h3 className="font-display font-semibold text-white">Weekly Community Digest</h3>
      </div>
      <p className="text-xs text-white/50 mb-3">
        Goes to <span className="text-white/70">every account — free and paying</span> automatically every Monday morning (Arizona time),
        with new cases, videos, upcoming events, and community activity.
      </p>

      {recipients && (
        <div className="mb-3" data-testid="digest-recipients">
          <button onClick={() => setShowList((v) => !v)} data-testid="digest-recipients-toggle"
            className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-hover transition-colors">
            <Users className="w-3.5 h-3.5" /> Goes to {recipients.count} {recipients.count === 1 ? "person" : "people"} — {showList ? "hide" : "see who"}
          </button>
          {showList && (
            <div className="mt-2 rounded-sm border border-white/10 bg-charcoal/40 divide-y divide-white/5 max-h-56 overflow-auto">
              {recipients.recipients.map((r, i) => (
                <div key={r.email + i} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs" data-testid={`recipient-${i}`}>
                  <span className="min-w-0 truncate"><span className="text-white/80">{r.name || "—"}</span> <span className="text-white/40 font-mono">{r.email}</span></span>
                  {r.email_status === "bounced"
                    ? <span className="shrink-0 text-red-400 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> bad email</span>
                    : <span className="shrink-0 text-green-400/70 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ok</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={sendNow}
          disabled={sendingNow}
          data-testid="digest-send-now-btn"
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm bg-gold text-charcoal font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50"
        >
          {sendingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
          Send now to everyone
        </button>
        <button
          onClick={sendTest}
          disabled={sendingTest}
          data-testid="digest-send-test-btn"
          className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-50"
        >
          {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send me a preview
        </button>
      </div>

      {log.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3" data-testid="digest-log">
          <div className="label-mono text-white/40 mb-2">Recent sends</div>
          <div className="space-y-1.5">
            {log.map((s) => (
              <div key={s.send_id} className="flex items-center justify-between gap-3 text-xs" data-testid={`digest-log-${s.send_id}`}>
                <span className="text-white/60 truncate">{fmtDate(s.sent_at)} · {s.triggered_by === "manual" ? "manual" : "auto"}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle2 className="w-3 h-3" /> {s.queued}</span>
                  {s.failed > 0 && <span className="inline-flex items-center gap-1 text-red-400"><AlertTriangle className="w-3 h-3" /> {s.failed}</span>}
                  <span className="text-white/40">of {s.total}</span>
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-white/40 leading-relaxed">
            "Accepted" = handed to the email service. Final delivery (and any <span className="text-white/60">suppressed / bounced</span> addresses) is shown in your Resend dashboard.
          </p>
        </div>
      )}
    </div>
  );
}
